// Post-processing — AgX tone mapping, bloom, gamma correction
// Engine code: renders to an HDR intermediate, then applies post-processing to screen.

import type { GPU } from "./gpu.js";

const BLOOM_PASSES = 5;

// Bloom downsample shader (write-only, no read_write needed)
const BLOOM_SHADER = /* wgsl */ `
@group(0) @binding(0) var srcTex: texture_2d<f32>;
@group(0) @binding(1) var dstTex: texture_storage_2d<rgba16float, write>;
@group(0) @binding(2) var srcSampler: sampler;

struct BloomParams { srcSize: vec2f, dstSize: vec2f, threshold: f32, _p1: f32, _p2: f32, _p3: f32 };
@group(0) @binding(3) var<uniform> params: BloomParams;

@compute @workgroup_size(8, 8)
fn downsample(@builtin(global_invocation_id) gid: vec3u) {
  let coord = vec2i(gid.xy);
  let size = vec2i(params.dstSize);
  if (coord.x >= size.x || coord.y >= size.y) { return; }

  let uv = (vec2f(coord) + 0.5) / params.dstSize;
  let texel = 1.0 / params.srcSize;

  var color = vec3f(0.0);
  color += textureSampleLevel(srcTex, srcSampler, uv + vec2f(-texel.x, -texel.y), 0.0).rgb;
  color += textureSampleLevel(srcTex, srcSampler, uv + vec2f( texel.x, -texel.y), 0.0).rgb;
  color += textureSampleLevel(srcTex, srcSampler, uv + vec2f(-texel.x,  texel.y), 0.0).rgb;
  color += textureSampleLevel(srcTex, srcSampler, uv + vec2f( texel.x,  texel.y), 0.0).rgb;
  color *= 0.25;

  // Threshold on first pass only
  if (params.threshold > 0.0) {
    let brightness = max(color.r, max(color.g, color.b));
    let contribution = max(0.0, brightness - params.threshold) / max(brightness, 0.001);
    color *= contribution;
  }

  textureStore(dstTex, coord, vec4f(color, 1.0));
}
`;

// Final composite: sample all bloom levels + AgX + gamma
const COMPOSITE_SHADER = /* wgsl */ `
@group(0) @binding(0) var hdrTex: texture_2d<f32>;
@group(0) @binding(1) var bloom0: texture_2d<f32>;
@group(0) @binding(2) var bloom1: texture_2d<f32>;
@group(0) @binding(3) var bloom2: texture_2d<f32>;
@group(0) @binding(4) var bloom3: texture_2d<f32>;
@group(0) @binding(5) var bloom4: texture_2d<f32>;
@group(0) @binding(6) var bloomSampler: sampler;
@group(0) @binding(7) var volTex: texture_2d<f32>;
@group(0) @binding(8) var cloudTex: texture_2d<f32>;
@group(0) @binding(9) var depthTex: texture_depth_2d;

struct PostParams { screenSize: vec2f, bloomStrength: f32, exposure: f32 };
@group(0) @binding(10) var<uniform> params: PostParams;

struct VSOut { @builtin(position) pos: vec4f, @location(0) uv: vec2f };

@vertex fn vsPost(@builtin(vertex_index) vi: u32) -> VSOut {
  let x = f32(i32(vi & 1u) * 4 - 1); let y = f32(i32(vi >> 1u) * 4 - 1);
  var o: VSOut; o.pos = vec4f(x, y, 0.0, 1.0); o.uv = vec2f((x + 1.0) * 0.5, (1.0 - y) * 0.5); return o;
}

fn linearToSrgb(c: vec3f) -> vec3f {
  let lo = c * 12.92;
  let hi = 1.055 * pow(c, vec3f(1.0 / 2.4)) - 0.055;
  return select(hi, lo, c <= vec3f(0.0031308));
}

// Dither to break up 8-bit banding in dark areas
fn dither(uv: vec2f) -> f32 {
  return (fract(sin(dot(uv, vec2f(12.9898, 78.233))) * 43758.5453) - 0.5) / 255.0;
}

// Bilateral upsample: sample 4 nearest low-res texels, weight by depth similarity.
// Prevents fog/cloud color bleeding across geometry edges.
fn bilateralSample(tex: texture_2d<f32>, fullCoord: vec2i, fullDepth: f32, scale: i32) -> vec3f {
  let lowCoord = fullCoord / scale;
  var totalWeight = 0.0;
  var result = vec3f(0.0);

  for (var dy = 0; dy <= 1; dy++) {
    for (var dx = 0; dx <= 1; dx++) {
      let sc = lowCoord + vec2i(dx, dy);
      let sample = textureLoad(tex, sc, 0).rgb;
      let lowFullCoord = sc * scale;
      let lowDepth = textureLoad(depthTex, lowFullCoord, 0);

      // Weight: similar depth = high weight, different depth = near zero
      let depthDiff = abs(fullDepth - lowDepth);
      let w = exp(-depthDiff * 200.0); // scaled for [0,1] depth range
      result += sample * w;
      totalWeight += w;
    }
  }

  if (totalWeight > 0.001) { return result / totalWeight; }
  return textureLoad(tex, lowCoord, 0).rgb;
}

fn bilateralSample4(tex: texture_2d<f32>, fullCoord: vec2i, fullDepth: f32, scale: i32) -> vec4f {
  let lowCoord = fullCoord / scale;
  var totalWeight = 0.0;
  var result = vec4f(0.0);

  for (var dy = 0; dy <= 1; dy++) {
    for (var dx = 0; dx <= 1; dx++) {
      let sc = lowCoord + vec2i(dx, dy);
      let sample = textureLoad(tex, sc, 0);
      let lowFullCoord = sc * scale;
      let lowDepth = textureLoad(depthTex, lowFullCoord, 0);

      let depthDiff = abs(fullDepth - lowDepth);
      let w = exp(-depthDiff * 200.0);
      result += sample * w;
      totalWeight += w;
    }
  }

  if (totalWeight > 0.001) { return result / totalWeight; }
  return textureLoad(tex, lowCoord, 0);
}

@fragment fn fsPost(in: VSOut) -> @location(0) vec4f {
  let coord = vec2i(in.pos.xy);
  var color = textureLoad(hdrTex, coord, 0).rgb;

  // Full-res depth for bilateral upsampling and sky detection
  let fullDepth = textureLoad(depthTex, coord, 0);

  // Clouds: bilinear upsample from quarter-res, masked at full-res by geometry depth.
  // Full-res masking = sharp pixel-perfect ocean/sky boundary, no blocky edges.
  let isSky = fullDepth >= 0.99999;
  if (isSky) {
    let cloud = textureSampleLevel(cloudTex, bloomSampler, in.uv, 0.0);
    color = cloud.rgb;
  }

  // Blend all bloom levels (wider = more weight for soft glow)
  var bloom = vec3f(0.0);
  bloom += textureSampleLevel(bloom0, bloomSampler, in.uv, 0.0).rgb * 0.5;
  bloom += textureSampleLevel(bloom1, bloomSampler, in.uv, 0.0).rgb * 0.3;
  bloom += textureSampleLevel(bloom2, bloomSampler, in.uv, 0.0).rgb * 0.2;
  bloom += textureSampleLevel(bloom3, bloomSampler, in.uv, 0.0).rgb * 0.15;
  bloom += textureSampleLevel(bloom4, bloomSampler, in.uv, 0.0).rgb * 0.1;
  color += bloom * params.bloomStrength;

  // Volumetric fog (quarter-res, bilateral upsample — applied after clouds, in front)
  color += bilateralSample(volTex, coord, fullDepth, 4);

  // Exposure
  color *= params.exposure;

  // Tone mapping: 1 - exp(-color) (Teardown-style)
  // Naturally crushes darks, smooth highlight rolloff, moody feel
  color = vec3f(1.0) - exp(-color);

  // sRGB gamma
  color = linearToSrgb(color);

  // Dither to prevent 8-bit banding in dark areas
  color += vec3f(dither(in.uv));

  return vec4f(color, 1.0);
}
`;

export type PostProcessConfig = {
  bloomStrength?: number;
  bloomThreshold?: number;
  exposure?: number;
};

export class PostProcess {
  hdrTarget: GPUTexture;

  private bloomChain: GPUTexture[] = [];
  private bloomSampler: GPUSampler;
  private downsamplePipeline: GPUComputePipeline;
  private compositePipeline: GPURenderPipeline;
  private downsampleBindGroups: GPUBindGroup[] = [];
  private compositeBindGroup: GPUBindGroup;
  private bloomParamsBuffers: GPUBuffer[] = [];
  private postParamsBuffer: GPUBuffer;
  private width: number;
  private height: number;

  constructor(private gpu: GPU, public config: PostProcessConfig = {}, sourceTexture?: GPUTexture, volumetricTexture?: GPUTexture, cloudTexture?: GPUTexture, depthTexture?: GPUTexture) {
    const { device, format, canvas } = gpu;
    this.width = canvas.width;
    this.height = canvas.height;

    this.hdrTarget = device.createTexture({
      size: [this.width, this.height],
      format: "rgba16float",
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_SRC,
    });

    this.bloomSampler = device.createSampler({ magFilter: "linear", minFilter: "linear" });

    // Bloom downsample chain
    let w = this.width >> 1, h = this.height >> 1;
    for (let i = 0; i < BLOOM_PASSES; i++) {
      this.bloomChain.push(device.createTexture({
        size: [Math.max(w, 1), Math.max(h, 1)],
        format: "rgba16float",
        usage: GPUTextureUsage.STORAGE_BINDING | GPUTextureUsage.TEXTURE_BINDING,
      }));
      w >>= 1; h >>= 1;
    }

    this.postParamsBuffer = device.createBuffer({ size: 16, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });

    // Downsample pipeline
    const bloomModule = device.createShaderModule({ code: BLOOM_SHADER });
    const bloomLayout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, texture: { sampleType: "float" } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, storageTexture: { access: "write-only", format: "rgba16float" } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, sampler: {} },
        { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },
      ],
    });
    this.downsamplePipeline = device.createComputePipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [bloomLayout] }),
      compute: { module: bloomModule, entryPoint: "downsample" },
    });

    // Per-pass params buffers + bind groups
    const threshold = this.config.bloomThreshold ?? 1.0;
    const bloomSource = sourceTexture ?? this.hdrTarget;
    const sources = [bloomSource, ...this.bloomChain.slice(0, -1)];
    let srcW2 = this.width, srcH2 = this.height;
    let dstW2 = this.width >> 1, dstH2 = this.height >> 1;
    for (let i = 0; i < BLOOM_PASSES; i++) {
      const buf = device.createBuffer({ size: 32, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
      device.queue.writeBuffer(buf, 0, new Float32Array([srcW2, srcH2, dstW2, dstH2, i === 0 ? threshold : 0, 0, 0, 0]));
      this.bloomParamsBuffers.push(buf);

      this.downsampleBindGroups.push(device.createBindGroup({
        layout: bloomLayout,
        entries: [
          { binding: 0, resource: sources[i].createView() },
          { binding: 1, resource: this.bloomChain[i].createView() },
          { binding: 2, resource: this.bloomSampler },
          { binding: 3, resource: { buffer: buf } },
        ],
      }));

      srcW2 = dstW2; srcH2 = dstH2;
      dstW2 >>= 1; dstH2 >>= 1;
    }

    // Composite pipeline
    const compModule = device.createShaderModule({ code: COMPOSITE_SHADER });
    const compLayout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },
        { binding: 1, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },
        { binding: 2, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },
        { binding: 3, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },
        { binding: 4, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },
        { binding: 5, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },
        { binding: 6, visibility: GPUShaderStage.FRAGMENT, sampler: {} },
        { binding: 7, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },
        { binding: 8, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },
        { binding: 9, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "depth" } },
        { binding: 10, visibility: GPUShaderStage.FRAGMENT, buffer: { type: "uniform" } },
      ],
    });
    this.compositePipeline = device.createRenderPipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [compLayout] }),
      vertex: { module: compModule, entryPoint: "vsPost" },
      fragment: { module: compModule, entryPoint: "fsPost", targets: [{ format }] },
      primitive: { topology: "triangle-list" },
    });

    // Default 1×1 black textures for optional passes
    const makePlaceholder = () => device.createTexture({
      size: [1, 1], format: "rgba16float",
      usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST,
    });
    const makeDepthPlaceholder = () => device.createTexture({
      size: [1, 1], format: "depth24plus",
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });
    const volTex = volumetricTexture ?? makePlaceholder();
    const cldTex = cloudTexture ?? makePlaceholder();
    const depthTex = depthTexture ?? makeDepthPlaceholder();

    this.compositeBindGroup = device.createBindGroup({
      layout: compLayout,
      entries: [
        { binding: 0, resource: bloomSource.createView() },
        { binding: 1, resource: this.bloomChain[0].createView() },
        { binding: 2, resource: this.bloomChain[1].createView() },
        { binding: 3, resource: this.bloomChain[2].createView() },
        { binding: 4, resource: this.bloomChain[3].createView() },
        { binding: 5, resource: this.bloomChain[4].createView() },
        { binding: 6, resource: this.bloomSampler },
        { binding: 7, resource: volTex.createView() },
        { binding: 8, resource: cldTex.createView() },
        { binding: 9, resource: depthTex.createView() },
        { binding: 10, resource: { buffer: this.postParamsBuffer } },
      ],
    });
  }

  render(encoder: GPUCommandEncoder): void {
    const { device, context } = this.gpu;

    // Update first pass threshold (may change from debug panel)
    const threshold = this.config.bloomThreshold ?? 1.0;
    device.queue.writeBuffer(this.bloomParamsBuffers[0], 16, new Float32Array([threshold]));

    // Bloom downsample chain
    let dstW = this.width >> 1, dstH = this.height >> 1;
    for (let i = 0; i < BLOOM_PASSES; i++) {
      const pass = encoder.beginComputePass();
      pass.setPipeline(this.downsamplePipeline);
      pass.setBindGroup(0, this.downsampleBindGroups[i]);
      pass.dispatchWorkgroups(Math.ceil(dstW / 8), Math.ceil(dstH / 8));
      pass.end();
      dstW >>= 1; dstH >>= 1;
    }

    // Final composite: AgX + bloom + gamma → swapchain
    const postParams = new Float32Array([
      this.width, this.height,
      this.config.bloomStrength ?? 0.04,
      this.config.exposure ?? 1.0,
    ]);
    device.queue.writeBuffer(this.postParamsBuffer, 0, postParams);

    const pass = encoder.beginRenderPass({
      colorAttachments: [{
        view: context.getCurrentTexture().createView(),
        clearValue: { r: 0, g: 0, b: 0, a: 1 },
        loadOp: "clear", storeOp: "store",
      }],
    });
    pass.setPipeline(this.compositePipeline);
    pass.setBindGroup(0, this.compositeBindGroup);
    pass.draw(3);
    pass.end();
  }
}
